import req from '../../api/request'
import { ENDPOINTS } from '../../api/endpoints'
import { toSpareCandidates } from '../../features/playerFinder/groupCandidates'
import type {
  SendAvailabilityRequestArgs,
  SpareCandidate,
  SpareCandidateFilters,
  SpareCandidatesResponse,
} from '../../features/playerFinder/types'
import { toPagePagination, type Page } from '../../ts-utils/pagedList'

/** Rows per page — the API's own default, sent so it can't drift. */
export const SPARE_CANDIDATES_PER_PAGE = 50

/**
 * SN-8309 — one page of the game's spare candidates: the server's eligibility
 * list for this game and team (roster spares, the competition's spare list,
 * public Player Finder profiles), with each candidate's ask status. The team's
 * roster spares are dropped by the mapping.
 */
export const getSpareCandidates = async ({
  gameId,
  teamId,
  date,
  positionId,
  genderId,
  location,
  page = 1,
}: SpareCandidateFilters & { page?: number }): Promise<
  Page<SpareCandidate>
> => {
  const response: SpareCandidatesResponse | undefined = await req(
    ENDPOINTS.GAMES.SPARE_CANDIDATES(gameId as string),
    {
      method: 'GET',
      query: {
        team_id: teamId,
        date,
        player_type_id: positionId,
        gender_id: genderId,
        // SN-8295 — where the players are (a listed player's card location, a
        // roster spare's profile address); a blank part isn't sent.
        country_id: location?.countryId || undefined,
        province_id: location?.provinceId || undefined,
        city: location?.city?.trim() || undefined,
        page,
        per_page: SPARE_CANDIDATES_PER_PAGE,
      },
    }
  )

  return {
    data: toSpareCandidates(response?.data),
    pagination: toPagePagination(response?.meta?.pagination, page),
  }
}

/**
 * How long one availability POST may take. The mobile message sheet stays
 * open, and can't be closed, while its send is out, so a hung request must end
 * in an error the sheet can show (with Retry) instead of a spinner. The POST
 * only records the asks (delivery runs on the server's queue), so this is a
 * safety net for a network that stopped answering, not a normal wait. A group
 * it cuts off ends the send there (sendInGroups), and the lists refresh after
 * every send, since the server may have recorded it.
 */
export const AVAILABILITY_REQUEST_TIMEOUT_MS = 30000

export const sendAvailabilityRequests = async ({
  gameId,
  teamId,
  playerIds,
  source,
  message,
}: SendAvailabilityRequestArgs): Promise<void> => {
  const trimmed = message?.trim()
  await req(ENDPOINTS.GAMES.AVAILABILITY_REQUESTS(gameId), {
    method: 'POST',
    timeout: AVAILABILITY_REQUEST_TIMEOUT_MS,
    // One attempt: the caller offers Retry (the server skips an ask that's
    // already open), and a transport retry would stretch the wait.
    maxAttempts: 1,
    // The Player Finder shows the failure itself.
    reportGlobalError: false,
    body: JSON.stringify({
      team_id: teamId,
      player_ids: playerIds,
      source,
      // Only include a message when the user actually typed one.
      ...(trimmed ? { message: trimmed } : {}),
    }),
  })
}
